/**
 * @file grabcad-mixin.ts
 *
 * GrabCAD environment detection for as-app. The GrabCAD protocol flows
 * (export TX, open-file RX, load-and-convert RX) are plain module functions in
 * features/importExport/lib/grabcad/grabcad.ts — import them directly (see
 * docs/export.md). Only the element-bound parts live here.
 *
 * Env detection itself (userData → env string) is fully generic — see
 * resolveSessionEnv in features/host/host.ts, called directly by as-app.ts — so
 * this mixin only holds the GrabCAD-specific convenience check, plus
 * setPersistentState/getPersistentState, which are element-bound for
 * convenience but no longer GrabCAD-specific (see db.ts's prefs store).
 *
 * Kept separate from the core app because GrabCAD is an optional integration
 * that only activates when window.opener is the GrabCAD host.
 */
import { internalData } from '../../../store/store.ts';
import { type Constructor } from './mixin-types-utils.ts';
import { getPref, setPref } from '../../../features/db/db.ts';

/** Surface added by GrabCADMixin. */
export interface GrabCADMixinMembers {
  /** True when running inside a GrabCAD-hosted environment. */
  isInGrabCAD(): boolean;
  /**
   * Persists a small key/value preference across sessions (e.g. a "don't show
   * again" flag). Stored in IndexedDB, and mirrored to the host's own store
   * where the env has one — see db.ts's prefs section. Not to be confused with
   * appData/customData, which is ephemeral app state that only survives a
   * reload once saved into a project.
   *
   * Resolves once the value is local. Any push to the host happens in the
   * background, so this doesn't wait on it.
   *
   * Safe to call at any point in app startup.
   */
  setPersistentState(state: string, value: string): Promise<void>;
  /**
   * Reads back a preference set via setPersistentState. Null if never set.
   *
   * A hit in the local store answers immediately. A miss waits briefly for the
   * startup pull from the host, if one is still in flight — on GrabCAD, where
   * IndexedDB is cleared between launches, that pull is the only thing that
   * tells a genuinely unset pref apart from one set in an earlier session.
   */
  getPersistentState(state: string): Promise<string | null>;
}

export const GrabCADMixin = <T extends Constructor<HTMLElement>>(Base: T) =>
  class GrabCADMixin extends Base {
    /** True when running inside a GrabCAD-hosted environment. */
    isInGrabCAD(): boolean {
      return internalData.appEnv === 'grabcad';
    }

    async setPersistentState(state: string, value: string): Promise<void> {
      return setPref(state, value);
    }

    async getPersistentState(state: string): Promise<string | null> {
      return getPref(state);
    }
  };
